import { useState, useEffect, useRef, useCallback } from 'react'

export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    // Return a cleanup function that will be called every time
    // when useEffect is re-called with a new value.
    return () => {
      clearTimeout(handler)
    }
  }, [delay, value])

  return debouncedValue
}

export const useCompare = value => {
  const prevValue = usePrevious(value)
  return prevValue !== value
}

// useEffect runs after 'render', so we are returning the prevValue
// before we store the new one
export const usePrevious = value => {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

export function useInterval(callback, delay) {
  const savedCallback = useRef()

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current()
    }
    if (delay !== null) {
      let id = setInterval(tick, delay)
      return () => clearInterval(id)
    }
  }, [delay])
}

export const useInput = (initialValue = '') => {
  const [value, setValue] = useState(initialValue)

  const reset = useCallback(() => setValue(''), [])
  const onChange = useCallback(event => setValue(event.target.value), [])

  return {
    value,
    setValue,
    reset,
    bind: {
      value,
      onChange,
    },
  }
}
